import React, { useState } from "react";
import { 
  FiUpload, 
  FiFileText, 
  FiImage, 
  FiShield, 
  FiCheckCircle, 
  FiAlertTriangle, 
  FiDownload, 
  FiEye, 
  FiTrash2, 
  FiLink, 
  FiRefreshCw
} from "react-icons/fi";
import type { RcaEvidenceItem } from "../../../types/rca.types.js";

export type RcaEvidenceCenterProps = {
  evidenceList?: RcaEvidenceItem[] | undefined;
  onUploadEvidence?: ((file: File, linkedTo?: string) => void) | undefined;
  onDeleteEvidence?: ((id: string) => void) | undefined;
  readOnly?: boolean | undefined;
};

const INITIAL_EVIDENCE: RcaEvidenceItem[] = [
  {
    id: "ev-1",
    fileName: "test-report.pdf",
    fileType: "PDF",
    fileSizeText: "2.1 MB",
    sizeBytes: 2202009,
    scanStatus: "Clean",
    linkedTo: "Why #3",
    uploadedBy: "John Smith",
    uploadedDate: "25-Aug-2026",
    sha256Hash: "a192c3d4e5f67890abcdef1234567890abcdef1234567890abcdef1234567890",
    mimeType: "application/pdf"
  },
  {
    id: "ev-2",
    fileName: "photo-1.jpg",
    fileType: "JPG",
    fileSizeText: "4.3 MB",
    sizeBytes: 4508876,
    scanStatus: "Clean",
    linkedTo: "Root Cause",
    uploadedBy: "John Smith",
    uploadedDate: "25-Aug-2026",
    sha256Hash: "b8374829faebd872619028475928374958273948572938475928374958273948",
    mimeType: "image/jpeg"
  },
  {
    id: "ev-3",
    fileName: "calibration-report.pdf",
    fileType: "PDF",
    fileSizeText: "1.6 MB",
    sizeBytes: 1677721,
    scanStatus: "Clean",
    linkedTo: "Fishbone",
    uploadedBy: "John Smith",
    uploadedDate: "25-Aug-2026",
    sha256Hash: "c928475829384759283749582739485729384759283749582739485729384759",
    mimeType: "application/pdf"
  },
  {
    id: "ev-4",
    fileName: "cmm-inspection-data.xlsx",
    fileType: "XLSX",
    fileSizeText: "850 KB",
    sizeBytes: 870400,
    scanStatus: "Scanning",
    linkedTo: "Why #1",
    uploadedBy: "Kumar",
    uploadedDate: "25-Aug-2026",
    sha256Hash: "d8374829faebd872619028475928374958273948572938475928374958273948",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  },
  {
    id: "ev-5",
    fileName: "suspicious-attachment.pdf",
    fileType: "PDF",
    fileSizeText: "1.2 MB",
    sizeBytes: 1258291,
    scanStatus: "Quarantined",
    linkedTo: "FTA",
    uploadedBy: "Priya",
    uploadedDate: "24-Aug-2026",
    sha256Hash: "e738495827394857293847592837495827394857293847592837495827394857",
    mimeType: "application/pdf"
  },
];

export function RcaEvidenceCenter({
  evidenceList = INITIAL_EVIDENCE,
  onUploadEvidence,
  onDeleteEvidence,
  readOnly = false,
}: RcaEvidenceCenterProps) {
  const [activeTab, setActiveTab] = useState<"ALL" | "LINKED" | "SCAN" | "DELETED">("ALL");
  const effectiveList = evidenceList && evidenceList.length > 0 ? evidenceList : INITIAL_EVIDENCE;
  const [selectedItem, setSelectedItem] = useState<RcaEvidenceItem>(effectiveList[0]!);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadFileName, setUploadFileName] = useState<string>("");
  const [uploadLinkedTo, setUploadLinkedTo] = useState<string>("Why #1");

  React.useEffect(() => {
    if (evidenceList && evidenceList.length > 0) {
      setSelectedItem(evidenceList[0]!);
    }
  }, [evidenceList]);

  const filtered = effectiveList.filter(item => {
    if (activeTab === "LINKED") return Boolean(item.linkedTo);
    if (activeTab === "SCAN") return item.scanStatus !== "Clean";
    if (activeTab === "DELETED") return item.isDeleted;
    return !item.isDeleted;
  });

  const getScanBadge = (status: RcaEvidenceItem["scanStatus"]) => {
    if (status === "Clean") {
      return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
          <FiCheckCircle className="w-3 h-3 text-emerald-600" /> Clean
        </span>
      );
    }
    if (status === "Scanning") {
      return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
          <FiRefreshCw className="w-3 h-3 text-amber-600 animate-spin" /> Scanning
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
        <FiShield className="w-3 h-3 text-rose-600" /> Quarantined
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top Header Card */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Evidence Management</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">5. Evidence Center</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Cryptographically hashed (SHA-256) immutable evidence files linked to specific RCA steps and root causes.
          </p>
        </div>

        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
          >
            <FiUpload className="w-4 h-4" />
            Upload Evidence
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center gap-2">
        {(["ALL", "LINKED", "SCAN", "DELETED"] as const).map(tabKey => (
          <button
            key={tabKey}
            type="button"
            onClick={() => setActiveTab(tabKey)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === tabKey
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {tabKey === "ALL" ? "All Evidence" :
             tabKey === "LINKED" ? "Linked to Analysis" :
             tabKey === "SCAN" ? "Scan Status" : "Deleted / Archive"}
          </button>
        ))}
      </div>

      {/* Evidence Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
              <tr>
                <th className="py-3 px-4">File Name</th>
                <th className="py-3 px-3">Type</th>
                <th className="py-3 px-3">Size</th>
                <th className="py-3 px-3">Scan Status</th>
                <th className="py-3 px-3">Linked To</th>
                <th className="py-3 px-3">Uploaded By</th>
                <th className="py-3 px-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(item => (
                <tr
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                    selectedItem?.id === item.id ? "bg-blue-50/60" : ""
                  }`}
                >
                  <td className="py-3.5 px-4 font-bold text-blue-600 flex items-center gap-2">
                    <FiFileText className="w-4 h-4 text-slate-400" />
                    {item.fileName}
                  </td>
                  <td className="py-3.5 px-3">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                      {item.fileType}
                    </span>
                  </td>
                  <td className="py-3.5 px-3 text-slate-600">{item.fileSizeText}</td>
                  <td className="py-3.5 px-3">{getScanBadge(item.scanStatus)}</td>
                  <td className="py-3.5 px-3">
                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1 w-fit">
                      <FiLink className="w-3 h-3" />
                      {item.linkedTo || "Unlinked"}
                    </span>
                  </td>
                  <td className="py-3.5 px-3 text-slate-600 font-medium">{item.uploadedBy}</td>
                  <td className="py-3.5 px-3 text-slate-500">{item.uploadedDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Item Detail Panel */}
      {selectedItem && (
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FiFileText className="w-4 h-4 text-blue-600" />
                {selectedItem.fileName}
              </h3>
              <p className="text-xs text-slate-500">
                Uploaded by {selectedItem.uploadedBy} on {selectedItem.uploadedDate} · {selectedItem.fileSizeText}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg flex items-center gap-1.5"
              >
                <FiDownload className="w-3.5 h-3.5" /> Download
              </button>
              <button
                type="button"
                className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg flex items-center gap-1.5"
              >
                <FiEye className="w-3.5 h-3.5" /> Preview
              </button>
            </div>
          </div>

          {/* Cryptographic SHA-256 Hash Display */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <FiShield className="w-3.5 h-3.5 text-blue-600" />
              Cryptographic Integrity Proof (SHA-256):
            </div>
            <div className="font-mono text-xs text-slate-700 break-all select-all font-semibold">
              {selectedItem.sha256Hash}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-slate-900">Upload RCA Evidence</h3>
            <p className="text-xs text-slate-500">
              Files will be scanned for viruses and assigned an immutable SHA-256 hash.
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Select File:</label>
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      if (!uploadFileName) setUploadFileName(file.name);
                    }
                  }}
                  className="w-full p-1.5 rounded-lg border border-slate-300 bg-slate-50 text-slate-700 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Display Name (Optional):</label>
                <input
                  type="text"
                  value={uploadFileName}
                  onChange={(e) => setUploadFileName(e.target.value)}
                  placeholder="e.g. CMM-inspection-log-2026.pdf"
                  className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Link to Investigation Step:</label>
                <select
                  value={uploadLinkedTo}
                  onChange={(e) => setUploadLinkedTo(e.target.value)}
                  className="w-full p-2 rounded-lg border border-slate-300 bg-white"
                >
                  <option value="Why #1">Why #1</option>
                  <option value="Why #2">Why #2</option>
                  <option value="Why #3">Why #3</option>
                  <option value="Why #4">Why #4</option>
                  <option value="Why #5">Why #5</option>
                  <option value="Fishbone">Fishbone Diagram</option>
                  <option value="Root Cause">Root Cause Verification</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setUploadFileName("");
                }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedFile}
                onClick={() => {
                  if (selectedFile) {
                    onUploadEvidence?.(selectedFile, uploadLinkedTo);
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setUploadFileName("");
                  }
                }}
                className={`px-4 py-2 rounded-lg text-xs font-semibold shadow-sm transition-all ${
                  selectedFile 
                    ? "bg-blue-600 hover:bg-blue-700 text-white" 
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}
              >
                Upload & Scan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
